import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    DatePicker,
    Form,
    Input,
    InputNumber,
    Modal,
    Select,
    Space,
    Table,
    Tag,
    Upload,
    message
} from 'antd';
import { DeleteOutlined, EyeOutlined, UploadOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import dayjs from 'dayjs';
import {
    deleteContractBillingDocument,
    deleteContractBillingPayment,
    getContractBillingMetrics,
    getContractBillingDocumentFile,
    getContractBillingDocuments,
    getContractBillingPaymentFile,
    getContractBillingPayments,
    postContractBillingDocument,
    postContractBillingPayment,
    postContractBillingDueEvaluation,
    putContractBillingDocument,
    putContractBillingDocumentMora
} from '../store/user/thunks';

type AnyRecord = Record<string, any>;

interface Props {
    organizationId: string;
    contractId: string;
    getContractBillingDocuments?: any;
    postContractBillingDocument?: any;
    putContractBillingDocument?: any;
    deleteContractBillingDocument?: any;
    getContractBillingDocumentFile?: any;
    getContractBillingPayments?: any;
    postContractBillingPayment?: any;
    putContractBillingDocumentMora?: any;
    deleteContractBillingPayment?: any;
    getContractBillingPaymentFile?: any;
    postContractBillingDueEvaluation?: any;
    getContractBillingMetrics?: any;
}

const pick = (source: AnyRecord | undefined, ...keys: string[]) => {
    if (!source) return undefined;
    for (const key of keys) {
        if (source[key] !== undefined && source[key] !== null) return source[key];
    }
    return undefined;
};

const dateText = (value?: string | null) => value ? new Date(value).toLocaleDateString('es-EC') : '—';

const money = (value: any) => `$${Number(value || 0).toFixed(2)}`;

const fileToBase64 = (file: File) => new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('No se pudo leer el archivo.'));
    reader.readAsDataURL(file);
});

const ContractBillingDocuments: React.FC<Props> = props => {
    const {
        organizationId,
        contractId,
        getContractBillingDocuments,
        postContractBillingDocument,
        putContractBillingDocument,
        deleteContractBillingDocument,
        getContractBillingDocumentFile,
        getContractBillingPayments,
        postContractBillingPayment,
        putContractBillingDocumentMora,
        deleteContractBillingPayment,
        getContractBillingPaymentFile,
        postContractBillingDueEvaluation,
        getContractBillingMetrics
    } = props;
    const [documents, setDocuments] = useState<AnyRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [file, setFile] = useState<File | null>(null);
    const [editing, setEditing] = useState<AnyRecord | null>(null);
    const [paymentModalOpen, setPaymentModalOpen] = useState(false);
    const [paymentDocument, setPaymentDocument] = useState<AnyRecord | null>(null);
    const [paymentFile, setPaymentFile] = useState<File | null>(null);
    const [paymentSaving, setPaymentSaving] = useState(false);
    const [paymentsModalOpen, setPaymentsModalOpen] = useState(false);
    const [payments, setPayments] = useState<AnyRecord[]>([]);
    const [paymentsLoading, setPaymentsLoading] = useState(false);
    const [moraModalOpen, setMoraModalOpen] = useState(false);
    const [moraDocument, setMoraDocument] = useState<AnyRecord | null>(null);
    const [moraSaving, setMoraSaving] = useState(false);
    const [metrics, setMetrics] = useState<AnyRecord | null>(null);
    const [evaluating, setEvaluating] = useState(false);
    const [form] = Form.useForm();
    const [paymentForm] = Form.useForm();
    const [moraForm] = Form.useForm();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getContractBillingDocuments?.(organizationId, contractId);
            setDocuments(Array.isArray(response) ? response : []);
        } finally {
            setLoading(false);
        }
    }, [contractId, getContractBillingDocuments, organizationId]);

    useEffect(() => { load(); }, [load]);

    const loadMetrics = useCallback(async () => {
        const response = await getContractBillingMetrics?.(organizationId, contractId);
        if (response && !Array.isArray(response)) setMetrics(response);
    }, [contractId, getContractBillingMetrics, organizationId]);

    useEffect(() => { loadMetrics(); }, [loadMetrics]);

    const closeModal = () => {
        setModalOpen(false);
        form.resetFields();
        setFile(null);
        setEditing(null);
    };

    const openCreate = () => {
        setEditing(null);
        form.resetFields();
        setFile(null);
        setModalOpen(true);
    };

    const openEdit = (document: AnyRecord) => {
        setEditing(document);
        setFile(null);
        form.setFieldsValue({
            billingPeriod: dayjs(String(pick(document, 'billingPeriod', 'BillingPeriod') || '')),
            invoiceNumber: pick(document, 'invoiceNumber', 'InvoiceNumber'),
            issueDate: dayjs(String(pick(document, 'issueDate', 'IssueDate') || '')),
            dueDate: pick(document, 'dueDate', 'DueDate') ? dayjs(String(pick(document, 'dueDate', 'DueDate'))) : null,
            amount: Number(pick(document, 'amount', 'Amount') || 0),
            notes: pick(document, 'notes', 'Notes') || ''
        });
        setModalOpen(true);
    };

    const submit = async () => {
        const values = await form.validateFields();
        if (!editing && !file) {
            message.error('Seleccione el RIDE en formato PDF.');
            return;
        }
        if (file && file.type && file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
            message.error('El RIDE debe ser un archivo PDF.');
            return;
        }

        setSaving(true);
        try {
            const payload: AnyRecord = {
                billingPeriod: values.billingPeriod.format('YYYY-MM'),
                invoiceNumber: values.invoiceNumber,
                issueDate: values.issueDate.format('YYYY-MM-DD'),
                dueDate: values.dueDate ? values.dueDate.format('YYYY-MM-DD') : null,
                amount: Number(values.amount || 0),
                currency: 'USD',
                notes: values.notes || null
            };
            if (file) {
                payload.rideBase64 = await fileToBase64(file);
                payload.rideFileName = file.name;
                payload.rideContentType = 'application/pdf';
            }
            const response = editing
                ? await putContractBillingDocument?.(organizationId, contractId, String(pick(editing, 'id', 'Id')), payload)
                : await postContractBillingDocument?.(organizationId, contractId, {
                    ...payload,
                    rideBase64: payload.rideBase64,
                    rideFileName: payload.rideFileName,
                    rideContentType: payload.rideContentType
                });
            if (Array.isArray(response)) setDocuments(response);
            closeModal();
        } finally {
            setSaving(false);
        }
    };

    const openFile = async (document: AnyRecord) => {
        const response = await getContractBillingDocumentFile?.(
            organizationId,
            contractId,
            String(pick(document, 'id', 'Id'))
        );
        const url = pick(response, 'uri', 'Uri', 'url', 'Url', 'blobUri', 'BlobUri');
        if (url) window.open(String(url), '_blank', 'noopener,noreferrer');
        else message.warning('No se recibió un enlace temporal para el RIDE.');
    };

    const inactivate = (document: AnyRecord) => {
        Modal.confirm({
            title: 'Anular RIDE',
            content: 'El registro quedará ANULADO y el archivo se conservará para trazabilidad. ¿Desea continuar?',
            okText: 'Anular',
            cancelText: 'Cancelar',
            okButtonProps: { danger: true },
            onOk: async () => {
                const response = await deleteContractBillingDocument?.(
                    organizationId,
                    contractId,
                    String(pick(document, 'id', 'Id'))
                );
                if (Array.isArray(response)) setDocuments(response);
            }
        });
    };

    const openPayment = (document: AnyRecord) => {
        setPaymentDocument(document);
        setPaymentFile(null);
        paymentForm.resetFields();
        const balance = Number(pick(document, 'balance', 'Balance') ?? pick(document, 'amount', 'Amount') ?? 0);
        paymentForm.setFieldsValue({ paymentDate: dayjs(), amount: balance > 0 ? balance : 0, paymentMethod: 'TRANSFERENCIA' });
        setPaymentModalOpen(true);
    };

    const closePayment = () => {
        setPaymentModalOpen(false);
        setPaymentDocument(null);
        setPaymentFile(null);
        paymentForm.resetFields();
    };

    const syncDocumentFromPayments = (documentId: string, paymentList: AnyRecord[]) => {
        const paidAmount = paymentList
            .filter(item => String(pick(item, 'status', 'Status') || 'APLICADO') === 'APLICADO')
            .reduce((total, item) => total + Number(pick(item, 'amount', 'Amount') || 0), 0);
        setDocuments(current => current.map(document => {
            if (String(pick(document, 'id', 'Id')) !== documentId) return document;
            const amount = Number(pick(document, 'amount', 'Amount') || 0);
            const balance = Math.max(0, amount - paidAmount);
            return {
                ...document,
                paidAmount,
                PaidAmount: paidAmount,
                balance,
                Balance: balance,
                status: balance <= 0.01 ? 'PAGADO' : 'PENDIENTE_PAGO',
                Status: balance <= 0.01 ? 'PAGADO' : 'PENDIENTE_PAGO'
            };
        }));
    };

    const submitPayment = async () => {
        if (!paymentDocument) return;
        const values = await paymentForm.validateFields();
        if (paymentFile && paymentFile.type && paymentFile.type !== 'application/pdf' && !paymentFile.name.toLowerCase().endsWith('.pdf')) {
            message.error('El comprobante debe ser un PDF.');
            return;
        }
        setPaymentSaving(true);
        try {
            const payload: AnyRecord = {
                paymentDate: values.paymentDate.format('YYYY-MM-DD'),
                amount: Number(values.amount || 0),
                paymentMethod: values.paymentMethod,
                paymentReference: values.paymentReference || null,
                notes: values.notes || null
            };
            if (paymentFile) {
                payload.proofBase64 = await fileToBase64(paymentFile);
                payload.proofFileName = paymentFile.name;
                payload.proofContentType = 'application/pdf';
            }
            const response = await postContractBillingPayment?.(
                organizationId,
                contractId,
                String(pick(paymentDocument, 'id', 'Id')),
                payload
            );
            if (Array.isArray(response)) {
                setPayments(response);
                syncDocumentFromPayments(String(pick(paymentDocument, 'id', 'Id')), response);
            }
            closePayment();
        } finally {
            setPaymentSaving(false);
        }
    };

    const openPayments = async (document: AnyRecord) => {
        setPaymentsLoading(true);
        try {
            const response = await getContractBillingPayments?.(organizationId, contractId, String(pick(document, 'id', 'Id')));
            setPayments(Array.isArray(response) ? response : []);
            setPaymentDocument(document);
            setPaymentsModalOpen(true);
        } finally {
            setPaymentsLoading(false);
        }
    };

    const openPaymentFile = async (payment: AnyRecord) => {
        if (!paymentDocument) return;
        const response = await getContractBillingPaymentFile?.(
            organizationId,
            contractId,
            String(pick(paymentDocument, 'id', 'Id')),
            String(pick(payment, 'id', 'Id'))
        );
        const url = pick(response, 'uri', 'Uri', 'url', 'Url', 'blobUri', 'BlobUri');
        if (url) window.open(String(url), '_blank', 'noopener,noreferrer');
        else message.warning('Este pago no tiene comprobante adjunto.');
    };

    const reversePayment = (payment: AnyRecord) => {
        if (!paymentDocument) return;
        Modal.confirm({
            title: 'Reversar pago',
            content: 'El pago quedará ANULADO y se recalculará el saldo. El registro se conservará para auditoría. ¿Desea continuar?',
            okText: 'Reversar',
            cancelText: 'Cancelar',
            okButtonProps: { danger: true },
            onOk: async () => {
                const response = await deleteContractBillingPayment?.(
                    organizationId,
                    contractId,
                    String(pick(paymentDocument, 'id', 'Id')),
                    String(pick(payment, 'id', 'Id'))
                );
                if (Array.isArray(response)) {
                    setPayments(response);
                    syncDocumentFromPayments(String(pick(paymentDocument, 'id', 'Id')), response);
                }
            }
        });
    };

    const openMora = (document: AnyRecord) => {
        setMoraDocument(document);
        moraForm.resetFields();
        setMoraModalOpen(true);
    };

    const submitMora = async () => {
        if (!moraDocument) return;
        const values = await moraForm.validateFields();
        setMoraSaving(true);
        try {
            const response = await putContractBillingDocumentMora?.(
                organizationId,
                contractId,
                String(pick(moraDocument, 'id', 'Id')),
                { reason: values.reason }
            );
            if (Array.isArray(response)) setDocuments(response);
            setMoraModalOpen(false);
            setMoraDocument(null);
            moraForm.resetFields();
        } finally {
            setMoraSaving(false);
        }
    };

    const evaluateDueDates = async () => {
        setEvaluating(true);
        try {
            const response = await postContractBillingDueEvaluation?.(organizationId, contractId);
            if (Array.isArray(response)) setDocuments(response);
            await loadMetrics();
        } finally { setEvaluating(false); }
    };

    return <Card
        title="RIDE del contrato comercial"
        extra={<Space wrap>
            <Button onClick={load}>Actualizar</Button>
            <Button loading={evaluating} onClick={evaluateDueDates}>Evaluar vencimientos</Button>
            <Button type="primary" icon={<UploadOutlined />} onClick={openCreate}>Cargar RIDE</Button>
        </Space>}
    >
        <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Carga manual temporal"
            description="Registre aquí el RIDE de cada período mientras la factura automática de sales_document no esté habilitada. El archivo se guarda en Azure y no reemplaza documentos legales ni el contrato firmado."
        />
        {metrics && <Card size="small" title="Resumen financiero" style={{ marginBottom: 16 }}>
            <Space wrap>
                <Tag color="blue">Facturado: {money(pick(metrics, 'totalInvoiced', 'TotalInvoiced'))}</Tag>
                <Tag color="green">Pagado: {money(pick(metrics, 'totalPaid', 'TotalPaid'))}</Tag>
                <Tag color="orange">Saldo: {money(pick(metrics, 'outstandingBalance', 'OutstandingBalance'))}</Tag>
                <Tag color="red">En mora: {pick(metrics, 'moraInvoiceCount', 'MoraInvoiceCount') || 0}</Tag>
                <Tag>Facturas: {pick(metrics, 'invoiceCount', 'InvoiceCount') || 0}</Tag>
            </Space>
        </Card>}
        <Table
            rowKey={item => String(pick(item, 'id', 'Id'))}
            loading={loading}
            dataSource={documents}
            pagination={{ pageSize: 8 }}
            locale={{ emptyText: 'No hay RIDE registrados para este contrato.' }}
            scroll={{ x: 1100 }}
            columns={[
                { title: 'Período', dataIndex: 'billingPeriod', render: (value: string) => value || '—' },
                { title: 'Factura', dataIndex: 'invoiceNumber' },
                { title: 'Emisión', dataIndex: 'issueDate', render: dateText },
                { title: 'Vencimiento', dataIndex: 'dueDate', render: dateText },
                { title: 'Valor', render: (_: any, row: AnyRecord) => `$${Number(pick(row, 'amount', 'Amount') || 0).toFixed(2)} ${pick(row, 'currency', 'Currency') || 'USD'}` },
                { title: 'Pagado', render: (_: any, row: AnyRecord) => money(pick(row, 'paidAmount', 'PaidAmount')) },
                { title: 'Saldo', render: (_: any, row: AnyRecord) => money(pick(row, 'balance', 'Balance')) },
                { title: 'Estado', dataIndex: 'status', render: (value: string) => <Tag color={value === 'ANULADO' || value === 'MORA' ? 'red' : value === 'PAGADO' ? 'green' : 'orange'}>{value || 'PENDIENTE_PAGO'}</Tag> },
                {
                    title: 'Acciones',
                    render: (_: any, row: AnyRecord) => {
                        const status = String(pick(row, 'status', 'Status') || 'PENDIENTE_PAGO');
                        const balance = Number(pick(row, 'balance', 'Balance') || 0);
                        return <Space wrap>
                            <Button size="small" icon={<EyeOutlined />} onClick={() => openFile(row)}>Ver RIDE</Button>
                            {status !== 'ANULADO' && balance > 0 && <Button size="small" type="primary" onClick={() => openPayment(row)}>Registrar pago</Button>}
                            {status !== 'ANULADO' && <Button size="small" onClick={() => openPayments(row)}>Ver pagos</Button>}
                            {status === 'PENDIENTE_PAGO' && <Button size="small" danger onClick={() => openMora(row)}>Marcar mora</Button>}
                            {status !== 'ANULADO' && <Button size="small" onClick={() => openEdit(row)}>Editar</Button>}
                            {status !== 'ANULADO' && <Button danger size="small" icon={<DeleteOutlined />} onClick={() => inactivate(row)}>Anular</Button>}
                        </Space>;
                    }
                }
            ]}
        />
        <Modal
            open={modalOpen}
            title={editing ? 'Editar RIDE del período' : 'Cargar RIDE del período'}
            okText={editing ? 'Guardar cambios' : 'Registrar RIDE'}
            cancelText="Cancelar"
            onCancel={closeModal}
            onOk={submit}
            confirmLoading={saving}
            destroyOnClose
        >
            <Alert type="warning" showIcon message="Validación obligatoria" description="Solo se aceptan PDF de hasta 10 MB. El período y número de factura no pueden duplicarse en el contrato." style={{ marginBottom: 16 }} />
            <Form form={form} layout="vertical">
                <Form.Item label="Período de facturación" name="billingPeriod" rules={[{ required: true, message: 'Seleccione el período' }]}>
                    <DatePicker picker="month" style={{ width: '100%' }} format="YYYY-MM" />
                </Form.Item>
                <Form.Item label="Número de factura" name="invoiceNumber" rules={[{ required: true, message: 'Ingrese el número de factura' }]}>
                    <Input placeholder="001-001-000000123" maxLength={100} />
                </Form.Item>
                <Space style={{ width: '100%' }} align="start">
                    <Form.Item label="Fecha de emisión" name="issueDate" rules={[{ required: true, message: 'Seleccione la fecha de emisión' }]}>
                        <DatePicker format="YYYY-MM-DD" />
                    </Form.Item>
                    <Form.Item label="Vencimiento" name="dueDate">
                        <DatePicker format="YYYY-MM-DD" />
                    </Form.Item>
                </Space>
                <Form.Item label="Valor total" name="amount" initialValue={0} rules={[{ required: true, message: 'Ingrese el valor' }]}>
                    <InputNumber min={0} precision={2} style={{ width: '100%' }} addonAfter="USD" />
                </Form.Item>
                <Form.Item label="Notas" name="notes"><Input.TextArea rows={2} maxLength={1000} /></Form.Item>
                <Upload
                    accept="application/pdf,.pdf"
                    maxCount={1}
                    beforeUpload={selected => { setFile(selected); return false; }}
                    onRemove={() => { setFile(null); return true; }}
                    fileList={file ? [file as any] : []}
                >
                    <Button icon={<UploadOutlined />}>Seleccionar PDF RIDE</Button>
                </Upload>
            </Form>
        </Modal>
        <Modal
            open={paymentModalOpen}
            title="Registrar pago del período"
            okText="Registrar pago"
            cancelText="Cancelar"
            onCancel={closePayment}
            onOk={submitPayment}
            confirmLoading={paymentSaving}
            destroyOnClose
        >
            <Alert type="info" showIcon style={{ marginBottom: 16 }} message="El pago no reemplaza la factura" description="Se registra como movimiento independiente, recalcula el saldo y queda disponible para auditoría y futura conciliación con sales_document." />
            <Form form={paymentForm} layout="vertical">
                <Form.Item label="Fecha de pago" name="paymentDate" rules={[{ required: true, message: 'Seleccione la fecha de pago' }]}>
                    <DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item label="Valor pagado" name="amount" rules={[{ required: true, message: 'Ingrese el valor pagado' }]}>
                    <InputNumber min={0.01} precision={2} style={{ width: '100%' }} addonAfter="USD" />
                </Form.Item>
                <Form.Item label="Método de pago" name="paymentMethod" rules={[{ required: true, message: 'Seleccione el método' }]}>
                    <Select options={[
                        { value: 'TRANSFERENCIA', label: 'Transferencia' },
                        { value: 'DEPOSITO', label: 'Depósito' },
                        { value: 'EFECTIVO', label: 'Efectivo' },
                        { value: 'TARJETA', label: 'Tarjeta' },
                        { value: 'OTRO', label: 'Otro' }
                    ]} />
                </Form.Item>
                <Form.Item label="Referencia" name="paymentReference"><Input maxLength={150} placeholder="Número de transferencia o comprobante" /></Form.Item>
                <Form.Item label="Notas" name="notes"><Input.TextArea rows={2} maxLength={1000} /></Form.Item>
                <Upload
                    accept="application/pdf,.pdf"
                    maxCount={1}
                    beforeUpload={selected => { setPaymentFile(selected); return false; }}
                    onRemove={() => { setPaymentFile(null); return true; }}
                    fileList={paymentFile ? [paymentFile as any] : []}
                >
                    <Button icon={<UploadOutlined />}>Adjuntar comprobante (opcional)</Button>
                </Upload>
            </Form>
        </Modal>
        <Modal
            open={paymentsModalOpen}
            title={`Pagos registrados${paymentDocument ? ` · ${pick(paymentDocument, 'invoiceNumber', 'InvoiceNumber') || ''}` : ''}`}
            footer={<Button onClick={() => setPaymentsModalOpen(false)}>Cerrar</Button>}
            onCancel={() => setPaymentsModalOpen(false)}
            width={850}
        >
            <Table
                rowKey={item => String(pick(item, 'id', 'Id'))}
                loading={paymentsLoading}
                dataSource={payments}
                pagination={false}
                locale={{ emptyText: 'No hay movimientos de pago registrados.' }}
                columns={[
                    { title: 'Fecha', render: (_: any, row: AnyRecord) => dateText(pick(row, 'paymentDate', 'PaymentDate')) },
                    { title: 'Valor', render: (_: any, row: AnyRecord) => money(pick(row, 'amount', 'Amount')) },
                    { title: 'Método', dataIndex: 'paymentMethod' },
                    { title: 'Referencia', dataIndex: 'paymentReference', render: (value: string) => value || '—' },
                    { title: 'Estado', render: (_: any, row: AnyRecord) => <Tag color={pick(row, 'status', 'Status') === 'ANULADO' ? 'red' : 'green'}>{pick(row, 'status', 'Status') || 'APLICADO'}</Tag> },
                    {
                        title: 'Acciones', render: (_: any, row: AnyRecord) => <Space>
                            {pick(row, 'proofFileName', 'ProofFileName') && <Button size="small" icon={<EyeOutlined />} onClick={() => openPaymentFile(row)}>Ver comprobante</Button>}
                            {String(pick(row, 'status', 'Status') || 'APLICADO') === 'APLICADO' && <Button danger size="small" onClick={() => reversePayment(row)}>Reversar</Button>}
                        </Space>
                    }
                ]}
            />
        </Modal>
        <Modal
            open={moraModalOpen}
            title="Marcar factura en mora"
            okText="Confirmar mora"
            cancelText="Cancelar"
            onCancel={() => { setMoraModalOpen(false); setMoraDocument(null); moraForm.resetFields(); }}
            onOk={submitMora}
            confirmLoading={moraSaving}
            destroyOnClose
        >
            <Alert type="warning" showIcon style={{ marginBottom: 16 }} message="La mora afecta el contrato" description="La factura pasará a MORA y el contrato quedará pendiente de pago. Al registrar el pago total se podrá reactivar." />
            <Form form={moraForm} layout="vertical">
                <Form.Item label="Motivo" name="reason" rules={[{ required: true, message: 'Ingrese el motivo de la mora' }]}>
                    <Input.TextArea rows={3} maxLength={500} placeholder="Ej. No se recibió el pago dentro del plazo acordado." />
                </Form.Item>
            </Form>
        </Modal>
    </Card>;
};

export default connect(null, {
    getContractBillingDocuments,
    postContractBillingDocument,
    putContractBillingDocument,
    deleteContractBillingDocument,
    getContractBillingDocumentFile,
    getContractBillingMetrics,
    getContractBillingPayments,
    postContractBillingPayment,
    postContractBillingDueEvaluation,
    putContractBillingDocumentMora,
    deleteContractBillingPayment,
    getContractBillingPaymentFile
})(ContractBillingDocuments);
